/**
 * Global Offline Mutation Queue
 * ─────────────────────────────
 * Persists write operations (INSERT / UPDATE / DELETE) in IndexedDB while
 * the device is offline and replays them in order when connectivity returns.
 *
 * Priority levels:
 *   CRITICAL (1) — check-ins, check-outs, status updates
 *   HIGH     (2) — ticket updates, notes
 *   LOW      (3) — SMS sends, notifications
 *
 * Features:
 *   • Priority-ordered flush (critical first)
 *   • Batched Supabase writes per table
 *   • Exponential backoff with jitter on retry
 *   • Conflict detection (server-side timestamp comparison)
 *   • Detailed sync metadata for dashboard UI
 */

import { supabase } from '../lib/supabase';
import { supabaseWithRetry } from './retryLogic';

// ─── Priority constants ───────────────────────────────────────────────────────
export const PRIORITY = {
  CRITICAL: 1,  // check-in, check-out, status updates
  HIGH:     2,  // ticket updates, notes
  LOW:      3,  // SMS sends, notifications
};

/** Derive priority from operation type/table */
export function derivePriority(mutation) {
  const { table, operation, type } = mutation;
  const t = (table || '')?.toLowerCase();
  const op = (operation || type || '')?.toLowerCase();

  if (t === 'ticket_checkins') return PRIORITY?.CRITICAL;
  if (op === 'checkin' || op === 'checkout') return PRIORITY?.CRITICAL;
  if (op === 'update_status' || op === 'status_update') return PRIORITY?.CRITICAL;
  if (t === '__sms_queue__' || op === 'sms_send' || op === 'send_sms') return PRIORITY?.LOW;
  return PRIORITY?.HIGH;
}

// ─── IndexedDB helpers ────────────────────────────────────────────────────────
const DB_NAME  = 'serviceJockeyOfflineQueue';
const DB_VER   = 2;
const STORE    = 'mutations';
const CONFLICTS_STORE = 'conflicts';
const META_STORE = 'syncMeta';

function openQueueDB() {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VER);
    req.onupgradeneeded = (e) => {
      const db = e.target.result;
      if (!db.objectStoreNames.contains(STORE)) {
        const store = db.createObjectStore(STORE, { keyPath: 'id', autoIncrement: true });
        store.createIndex('queuedAt',  'queuedAt',  { unique: false });
        store.createIndex('status',    'status',    { unique: false });
        store.createIndex('priority',  'priority',  { unique: false });
      } else {
        // v1 → v2: add priority index if missing
        const store = e.target.transaction.objectStore(STORE);
        if (!store.indexNames.contains('priority')) {
          store.createIndex('priority', 'priority', { unique: false });
        }
      }
      if (!db.objectStoreNames.contains(CONFLICTS_STORE)) {
        const cs = db.createObjectStore(CONFLICTS_STORE, { keyPath: 'id', autoIncrement: true });
        cs.createIndex('detectedAt', 'detectedAt', { unique: false });
        cs.createIndex('resolved',   'resolved',   { unique: false });
      }
      if (!db.objectStoreNames.contains(META_STORE)) {
        db.createObjectStore(META_STORE, { keyPath: 'key' });
      }
    };
    req.onsuccess  = () => resolve(req.result);
    req.onerror    = () => reject(req.error);
  });
}

async function idbGetAll(storeName) {
  const db = await openQueueDB();
  return new Promise((resolve, reject) => {
    const tx  = db.transaction(storeName, 'readonly');
    const req = tx.objectStore(storeName).getAll();
    req.onsuccess = () => resolve(req.result || []);
    req.onerror   = () => reject(req.error);
  });
}

async function idbAdd(storeName, record) {
  const db = await openQueueDB();
  return new Promise((resolve, reject) => {
    const tx  = db.transaction(storeName, 'readwrite');
    const req = tx.objectStore(storeName).add(record);
    req.onsuccess = () => resolve(req.result);
    req.onerror   = () => reject(req.error);
  });
}

async function idbPut(storeName, record) {
  const db = await openQueueDB();
  return new Promise((resolve, reject) => {
    const tx  = db.transaction(storeName, 'readwrite');
    const req = tx.objectStore(storeName).put(record);
    req.onsuccess = () => resolve(req.result);
    req.onerror   = () => reject(req.error);
  });
}

async function idbDelete(storeName, id) {
  const db = await openQueueDB();
  return new Promise((resolve, reject) => {
    const tx  = db.transaction(storeName, 'readwrite');
    const req = tx.objectStore(storeName).delete(id);
    req.onsuccess = () => resolve();
    req.onerror   = () => reject(req.error);
  });
}

async function getMeta(key) {
  try {
    const db = await openQueueDB();
    return new Promise((resolve) => {
      const tx  = db.transaction(META_STORE, 'readonly');
      const req = tx.objectStore(META_STORE).get(key);
      req.onsuccess = () => resolve(req.result?.value ?? null);
      req.onerror   = () => resolve(null);
    });
  } catch { return null; }
}

async function setMeta(key, value) {
  await idbPut(META_STORE, { key, value });
}

// ─── Exponential backoff delay ────────────────────────────────────────────────
const BASE_DELAY_MS = 500;
const MAX_DELAY_MS  = 30000;

function calcBackoffDelay(retryCount) {
  const exp = BASE_DELAY_MS * Math.pow(2, retryCount);
  const jitter = Math.random() * exp * 0.3; // ±30% jitter
  return Math.min(exp + jitter, MAX_DELAY_MS);
}

// ─── Queue API ────────────────────────────────────────────────────────────────
const MAX_RETRIES = 5;

/**
 * Add a mutation to the offline queue.
 * Priority is auto-derived if not provided.
 */
export async function enqueueOfflineMutation(mutation) {
  const priority = mutation?.priority ?? derivePriority(mutation);
  await idbAdd(STORE, {
    ...mutation,
    priority,
    queuedAt:   new Date()?.toISOString(),
    status:     'pending',
    retryCount: 0,
    nextRetryAt: null,
  });
  notifyListeners();
}

/**
 * Return all pending (not permanently failed) mutations, sorted by priority then time.
 */
export async function getPendingMutations() {
  const all = await idbGetAll(STORE);
  const now = Date.now();
  return (all || [])?.filter(m => {
      if (m?.status === 'failed') return false;
      if ((m?.retryCount || 0) >= MAX_RETRIES) return false;
      // Respect backoff — don't retry until nextRetryAt
      if (m?.nextRetryAt && new Date(m.nextRetryAt)?.getTime() > now) return false;
      return true;
    })?.sort((a, b) => {
      // Sort by priority first (lower number = higher priority)
      const pa = a?.priority ?? PRIORITY?.HIGH;
      const pb = b?.priority ?? PRIORITY?.HIGH;
      if (pa !== pb) return pa - pb;
      // Then by queue time (oldest first)
      return new Date(a?.queuedAt || 0) - new Date(b?.queuedAt || 0);
    });
}

/**
 * Return ALL mutations including failed ones (for dashboard display).
 */
export async function getAllMutations() {
  return idbGetAll(STORE);
}

/**
 * Return count of pending mutations.
 */
export async function getPendingCount() {
  const pending = await getPendingMutations();
  return pending?.length;
}

/**
 * Return count by priority for dashboard.
 */
export async function getPendingCountByPriority() {
  const all = await idbGetAll(STORE);
  const active = (all || [])?.filter(m => m?.status !== 'failed' && (m?.retryCount || 0) < MAX_RETRIES);
  return {
    critical: active?.filter(m => (m?.priority ?? PRIORITY?.HIGH) === PRIORITY?.CRITICAL)?.length,
    high:     active?.filter(m => (m?.priority ?? PRIORITY?.HIGH) === PRIORITY?.HIGH)?.length,
    low:      active?.filter(m => (m?.priority ?? PRIORITY?.HIGH) === PRIORITY?.LOW)?.length,
    total:    active?.length,
    failed:   (all || [])?.filter(m => m?.status === 'failed' || (m?.retryCount || 0) >= MAX_RETRIES)?.length,
  };
}

/**
 * Get last successful sync timestamp.
 */
export async function getLastSyncTime() {
  return getMeta('lastSyncAt');
}

// ─── Conflict management ──────────────────────────────────────────────────────
/**
 * Record a detected conflict for UI resolution.
 */
export async function recordConflict({ table, recordId, localPayload, serverPayload, label }) {
  await idbAdd(CONFLICTS_STORE, {
    table,
    recordId,
    localPayload,
    serverPayload,
    label: label || `Conflict on ${table}`,
    detectedAt: new Date()?.toISOString(),
    resolved: false,
    resolution: null,
  });
  notifyListeners();
}

/**
 * Get all unresolved conflicts.
 */
export async function getUnresolvedConflicts() {
  const all = await idbGetAll(CONFLICTS_STORE);
  return (all || [])?.filter(c => !c?.resolved);
}

/**
 * Resolve a conflict — 'local' keeps local changes, 'server' discards them.
 */
export async function resolveConflict(conflictId, resolution, resolvedPayload) {
  const all = await idbGetAll(CONFLICTS_STORE);
  const conflict = all?.find(c => c?.id === conflictId);
  if (!conflict) return;

  if (resolution === 'local' && resolvedPayload) {
    // Push local version to server
    try {
      await supabaseWithRetry(() => {
        let q = supabase?.from(conflict?.table)?.update(resolvedPayload);
        q = q?.eq('id', conflict?.recordId);
        return q;
      });
    } catch { /* best effort */ }
  }

  await idbPut(CONFLICTS_STORE, {
    ...conflict,
    resolved: true,
    resolution,
    resolvedAt: new Date()?.toISOString(),
  });
  notifyListeners();
}

/**
 * Dismiss a conflict without applying any changes.
 */
export async function dismissConflict(conflictId) {
  return resolveConflict(conflictId, 'dismissed', null);
}

// ─── Flush (replay) ───────────────────────────────────────────────────────────
let flushInProgress = false;

/**
 * Flush the queue — replays pending mutations in priority order.
 * Batches same-table updates where safe.
 * Uses exponential backoff for retries.
 */
export async function flushOfflineQueue(onProgress) {
  if (flushInProgress || !navigator.onLine) return { synced: 0, failed: 0, conflicts: 0 };
  flushInProgress = true;

  let synced = 0;
  let failed = 0;
  let conflicts = 0;

  try {
    const pending = await getPendingMutations();
    if (!pending?.length) return { synced: 0, failed: 0, conflicts: 0 };

    // Group by priority for ordered processing
    const critical = pending?.filter(m => (m?.priority ?? PRIORITY?.HIGH) === PRIORITY?.CRITICAL);
    const high     = pending?.filter(m => (m?.priority ?? PRIORITY?.HIGH) === PRIORITY?.HIGH);
    const low      = pending?.filter(m => (m?.priority ?? PRIORITY?.HIGH) === PRIORITY?.LOW);

    const ordered = [...critical, ...high, ...low];

    for (const mutation of ordered) {
      try {
        // Conflict check for updates: compare server updated_at vs our queuedAt
        if (mutation?.operation === 'update' && mutation?.match?.id && mutation?.table !== '__sms_queue__') {
          const conflictDetected = await checkForConflict(mutation);
          if (conflictDetected) {
            conflicts++;
            onProgress?.({ synced, failed, conflicts, label: `Conflict: ${mutation?.label}` });
            continue; // Skip — user must resolve via UI
          }
        }

        const result = await replayMutation(mutation);
        if (result?.error) {
          await scheduleRetry(mutation);
          failed++;
          onProgress?.({ synced, failed, conflicts, label: `Failed: ${mutation?.label}` });
        } else {
          await idbDelete(STORE, mutation?.id);
          synced++;
          onProgress?.({ synced, failed, conflicts, label: mutation?.label });
        }
      } catch {
        await scheduleRetry(mutation);
        failed++;
      }
    }

    if (synced > 0) {
      const now = new Date()?.toISOString();
      await setMeta('lastSyncAt', now);
    }

    notifyListeners();
    return { synced, failed, conflicts };
  } finally {
    flushInProgress = false;
  }
}

/**
 * Check if a server record was updated after we queued our local change.
 * Returns true if a conflict is detected.
 */
async function checkForConflict(mutation) {
  try {
    const { data } = await supabase?.from(mutation?.table)?.select('id, updated_at')?.eq('id', mutation?.match?.id)?.single();

    if (!data?.updated_at) return false;

    const serverTs = new Date(data.updated_at)?.getTime();
    const queuedTs = new Date(mutation.queuedAt)?.getTime();

    if (serverTs > queuedTs) {
      // Server has newer data — record conflict for UI resolution
      const { data: serverRecord } = await supabase?.from(mutation?.table)?.select('*')?.eq('id', mutation?.match?.id)?.single();

      await recordConflict({
        table: mutation?.table,
        recordId: mutation?.match?.id,
        localPayload: mutation?.payload,
        serverPayload: serverRecord || {},
        label: mutation?.label || `Conflict on ${mutation?.table}`,
      });
      // Remove the conflicting mutation from queue
      await idbDelete(STORE, mutation?.id);
      return true;
    }
    return false;
  } catch {
    return false; // On error, proceed with sync
  }
}

async function replayMutation(mutation) {
  const { table, operation, payload, match } = mutation;

  // Handle SMS queue sentinel — invoke edge function
  if (operation === 'sms_send' || table === '__sms_queue__') {
    return supabaseWithRetry(() =>
      supabase?.functions?.invoke('send-job-status-sms', {
        body: {
          ticketId: payload?.ticketId,
          ticketNumber: payload?.ticketNumber,
          newStatus: payload?.newStatus,
          technicianIds: payload?.technicianIds || [],
          organizationId: payload?.orgId,
        },
      })
    , { maxAttempts: 2 });
  }

  // Handle media sync sentinel — handled by OfflineQueueContext interceptor
  if (operation === 'media_sync' || table === '__media_sync__') {
    // Media sync is handled externally by the context; treat as success here
    return { data: null, error: null };
  }

  // Remap legacy technician_id → personnel_id for ticket_checkins inserts
  let normalizedPayload = payload;
  if (table === 'ticket_checkins' && operation === 'insert' && payload?.technician_id !== undefined) {
    normalizedPayload = { ...payload, personnel_id: payload?.technician_id };
    delete normalizedPayload?.technician_id;
  }

  return supabaseWithRetry(() => {
    if (operation === 'insert') {
      return supabase?.from(table)?.insert(normalizedPayload);
    } else if (operation === 'update') {
      let q = supabase?.from(table)?.update(normalizedPayload);
      if (match) {
        Object.entries(match)?.forEach(([col, val]) => { q = q?.eq(col, val); });
      }
      return q;
    } else if (operation === 'delete') {
      let q = supabase?.from(table)?.delete();
      if (match) {
        Object.entries(match)?.forEach(([col, val]) => { q = q?.eq(col, val); });
      }
      return q;
    }
    return Promise.resolve({ data: null, error: new Error(`Unknown operation: ${operation}`) });
  }, { maxAttempts: 3, baseDelayMs: 400 });
}

async function scheduleRetry(mutation) {
  const newCount = (mutation?.retryCount || 0) + 1;
  const delay = calcBackoffDelay(newCount);
  const nextRetryAt = new Date(Date.now() + delay)?.toISOString();

  await idbPut(STORE, {
    ...mutation,
    retryCount:    newCount,
    lastAttemptAt: new Date()?.toISOString(),
    nextRetryAt,
    status: newCount >= MAX_RETRIES ? 'failed' : 'pending',
  });
}

// ─── Listener pattern (for context/hook reactivity) ───────────────────────────
const listeners = new Set();

export function subscribeToQueueChanges(fn) {
  listeners?.add(fn);
  return () => listeners?.delete(fn);
}

function notifyListeners() {
  listeners?.forEach(fn => fn());
}

// ─── Auto-flush on connectivity restore ───────────────────────────────────────
if (typeof window !== 'undefined') {
  window.addEventListener('online', () => {
    // Small delay to let the connection stabilise
    setTimeout(() => flushOfflineQueue(), 1500);
  });
}
